import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import SearchableSelect from '../components/SearchableSelect';
import { Label } from '../components/ui/label';
import { Plus, Trash, PencilSimple } from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const STATUS = [
  { v: 'active', l: 'Active', c: 'bg-emerald-100 text-emerald-700' },
  { v: 'on_hold', l: 'On Hold', c: 'bg-amber-100 text-amber-700' },
  { v: 'completed', l: 'Completed', c: 'bg-blue-100 text-blue-700' },
  { v: 'cancelled', l: 'Cancelled', c: 'bg-rose-100 text-rose-700' },
];

const emptyForm = () => ({
  name: '', code: '', client_id: '', description: '',
  manager_user_id: '', department_id: '', members: [],
  start_date: '', end_date: '', status: 'active', priority: 'medium',
  is_recurring: false, recurrence: 'monthly', recurrence_day: 1,
  contract_value: 0, contract_currency: 'INR',
});

export default function ProjectsPage() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [users, setUsers] = useState([]);
  const [billing, setBilling] = useState({});  // {project_id: billing_summary}
  const [show, setShow] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const isAdmin = user?.role === 'admin';
  const canManage = isAdmin || user?.is_team_lead || (user?.extra_modules || []).includes('projects');

  const load = async () => {
    const cfg = { withCredentials: true };
    const [pj, cl, de, em, us, bl] = await Promise.all([
      axios.get(`${API_URL}/api/projects`, cfg),
      axios.get(`${API_URL}/api/clients`, cfg).catch(() => ({ data: [] })),
      axios.get(`${API_URL}/api/departments`, cfg).catch(() => ({ data: [] })),
      axios.get(`${API_URL}/api/employees`, cfg).catch(() => ({ data: [] })),
      isAdmin ? axios.get(`${API_URL}/api/users`, cfg).catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
      axios.get(`${API_URL}/api/projects-billing/summary`, cfg).catch(() => ({ data: [] })),
    ]);
    setItems(pj.data || []);
    setClients(cl.data || []);
    setDepartments(de.data || []);
    setEmployees(em.data || []);
    setUsers(us.data || []);
    setBilling(Object.fromEntries((bl.data || []).map((b) => [b.project_id, b])));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const startNew = () => { setEditing(null); setForm(emptyForm()); setShow(true); };
  const startEdit = (p) => { setEditing(p); setForm({ ...emptyForm(), ...p }); setShow(true); };  const submit = async () => {
    if (!form.name.trim()) return alert('Project name is required');
    try {
      if (editing) await axios.put(`${API_URL}/api/projects/${editing.id}`, form, { withCredentials: true });
      else         await axios.post(`${API_URL}/api/projects`, form, { withCredentials: true });
      setShow(false); load();
    } catch (e) { alert(e.response?.data?.detail || e.message); }
  };
  const remove = async (p) => {
    if (!window.confirm(`Delete project "${p.name}" and ALL its tasks? This cannot be undone.`)) return;
    await axios.delete(`${API_URL}/api/projects/${p.id}`, { withCredentials: true });
    load();
  };

  // Show "Company — Contact Name" for client labels (falls back gracefully if a field is missing)
  const clientLabel = (c) => {
    if (!c) return '—';
    const comp = (c.company_name || c.company || '').trim();
    const person = `${c.first_name || ''} ${c.last_name || ''}`.trim();
    if (comp && person) return `${comp} — ${person}`;
    return comp || person || c.email || '—';
  };
  const clientName = (id) => clientLabel(clients.find((c) => c.id === id));
  const deptName = (id) => (departments.find((d) => d.id === id)?.name || '—');

  return (
    <div className="space-y-4 p-6" data-testid="projects-page">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold" style={{ color: '#1a2e4a' }}>Projects</h2>
        {canManage && (
          <Button onClick={startNew} data-testid="new-project-btn">
            <Plus size={14} className="mr-1.5" /> New Project
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {items.length === 0 && (
          <Card className="p-6 col-span-full text-center text-sm text-gray-500">
            No projects yet. {canManage ? 'Click "New Project" to create one.' : ''}
          </Card>
        )}
        {items.map((p) => {
          const st = STATUS.find((s) => s.v === p.status) || STATUS[0];
          return (
            <Card key={p.id} className="p-4 hover:shadow-md transition-shadow" data-testid={`project-card-${p.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-gray-500 uppercase tracking-wide">{p.code || '—'}</div>
                  <div className="text-base font-bold mt-0.5" style={{ color: '#1a2e4a' }}>{p.name}</div>
                  <div className="text-xs text-gray-600 mt-1">Client: <span className="font-medium">{clientName(p.client_id)}</span></div>
                  {p.department_id && <div className="text-xs text-gray-600">Dept: {deptName(p.department_id)}</div>}
                  <div className="text-xs text-gray-600">Members: {(p.members || []).length}</div>
                  <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                    <span className={`inline-block text-[10px] font-bold px-2 py-0.5 rounded ${st.c}`}>{st.l.toUpperCase()}</span>
                    {p.is_recurring && (
                      <span className="inline-block text-[10px] font-bold px-2 py-0.5 rounded bg-violet-100 text-violet-700" title={`Recurs ${p.recurrence || 'monthly'}`}>
                        🔁 {(p.recurrence || 'MONTHLY').toUpperCase()}
                      </span>
                    )}
                  </div>
                </div>
                {canManage && (
                  <div className="flex flex-col gap-1">
                    <button onClick={() => startEdit(p)} className="text-blue-600 hover:bg-blue-50 p-1 rounded"><PencilSimple size={14} /></button>
                    <button onClick={() => remove(p)} className="text-rose-600 hover:bg-rose-50 p-1 rounded"><Trash size={14} /></button>
                  </div>
                )}
              </div>
              {p.description && <p className="text-xs text-gray-600 mt-2 line-clamp-2">{p.description}</p>}
              <div className="text-[11px] text-gray-500 mt-2">
                {p.start_date && <>📅 {p.start_date}</>} {p.end_date && <>→ {p.end_date}</>}
              </div>
              {/* Billing progress strip — only for non-recurring projects with contract value */}
              {!p.is_recurring && billing[p.id] && billing[p.id].contract_value > 0 && (() => {
                const b = billing[p.id];
                const barPct = Math.min(100, b.pct);
                const barColor = b.over_budget ? 'bg-rose-500' : b.pct >= 80 ? 'bg-amber-500' : b.pct >= 40 ? 'bg-blue-500' : 'bg-emerald-500';
                const fmt = (n) => new Intl.NumberFormat('en-IN').format(Math.round(n));
                const ccy = b.contract_currency || 'INR';
                const downloadStatement = async () => {
                  try {
                    const r = await axios.get(`${API_URL}/api/projects/${p.id}/statement.pdf`, {
                      withCredentials: true, responseType: 'blob',
                    });
                    const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `statement_${p.code || p.name}.pdf`;
                    document.body.appendChild(a); a.click();
                    a.remove(); window.URL.revokeObjectURL(url);
                  } catch (e) {
                    alert('Statement failed: ' + (e.response?.data?.detail || e.message));
                  }
                };
                return (
                  <div className="mt-3 pt-2 border-t" data-testid={`billing-strip-${p.id}`}>
                    <div className="flex items-center justify-between text-[11px] mb-1">
                      <span className="font-semibold text-gray-700">💰 Billing Progress</span>
                      <div className="flex items-center gap-2">
                        <span className={`font-bold ${b.over_budget ? 'text-rose-700' : 'text-gray-700'}`}>{b.pct}%{b.over_budget && ' ⚠️ over'}</span>
                        <button
                          onClick={downloadStatement}
                          className="text-[10px] px-2 py-0.5 rounded bg-emerald-600 text-white hover:bg-emerald-700 font-semibold"
                          title="Download client-facing PDF statement"
                          data-testid={`statement-btn-${p.id}`}
                        >
                          📄 Statement
                        </button>
                      </div>
                    </div>
                    <div className="h-2 bg-gray-100 rounded overflow-hidden">
                      <div className={`h-full transition-all ${barColor}`} style={{ width: `${barPct}%` }} />
                    </div>
                    <div className="text-[10px] text-gray-600 mt-1.5 space-y-0.5">
                      <div>
                        <span className="font-semibold">{ccy} {fmt(b.contract_value)}</span> contract ·
                        <span className="text-blue-700 font-semibold"> {fmt(b.invoiced_inr)}</span> invoiced ({b.invoice_count} inv)
                      </div>
                      <div>
                        <span className="text-emerald-700 font-semibold">✓ {fmt(b.paid_inr)}</span> received ·
                        <span className={`font-semibold ${(b.invoiced_inr - b.paid_inr) > 0 ? 'text-rose-700' : 'text-gray-500'}`}> {fmt(Math.max(0, b.invoiced_inr - b.paid_inr))}</span> due from client ·
                        <span className="text-purple-700 font-semibold"> {fmt(b.pending_inr)}</span> yet to invoice
                      </div>
                    </div>
                  </div>
                );
              })()}
            </Card>
          );
        })}
      </div>

      {show && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center" onClick={() => setShow(false)}>
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto p-5" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-bold mb-3" style={{ color: '#1a2e4a' }}>{editing ? 'Edit' : 'New'} Project</h3>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label className="text-xs">Project name *</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="project-name" />
              </div>
              <div>
                <Label className="text-xs">Code</Label>
                <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="e.g. WEB-2026-01" />
              </div>
              <div>
                <Label className="text-xs">Client</Label>
                <SearchableSelect
                  value={form.client_id}
                  onChange={(v) => setForm({ ...form, client_id: v })}
                  options={[{ value: '', label: '— No client —' }, ...clients.map((c) => ({ value: c.id, label: clientLabel(c), search: `${c.first_name || ''} ${c.last_name || ''} ${c.company_name || ''} ${c.email || ''}` }))]}
                  placeholder="Pick a client…"
                  testId="project-client"
                />
              </div>
              <div>
                <Label className="text-xs">Manager (user)</Label>
                <SearchableSelect
                  value={form.manager_user_id}
                  onChange={(v) => setForm({ ...form, manager_user_id: v })}
                  options={[{ value: '', label: '— None —' }, ...users.map((u) => ({ value: u.id, label: u.name, sublabel: u.role }))]}
                  placeholder="Pick a manager…"
                  testId="project-manager"
                />
              </div>
              <div>
                <Label className="text-xs">Department</Label>
                <SearchableSelect
                  value={form.department_id}
                  onChange={(v) => setForm({ ...form, department_id: v })}
                  options={[{ value: '', label: '— None —' }, ...departments.map((d) => ({ value: d.id, label: d.name }))]}
                  placeholder="Pick a department…"
                  testId="project-department"
                />
              </div>
              <div>
                <Label className="text-xs">Status</Label>
                <select className="w-full border rounded px-3 py-2 text-sm bg-white" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                  {STATUS.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-xs">Priority</Label>
                <select className="w-full border rounded px-3 py-2 text-sm bg-white" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })}>
                  <option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option><option value="urgent">Urgent</option>
                </select>
              </div>
              <div>
                <Label className="text-xs">Start date</Label>
                <Input type="date" value={form.start_date || ''} onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">End date</Label>
                <Input type="date" value={form.end_date || ''} onChange={(e) => setForm({ ...form, end_date: e.target.value })} />
              </div>
              <div className="col-span-2">
                <Label className="text-xs">Team members (employees)</Label>
                <div className="max-h-32 overflow-y-auto border rounded p-2 bg-white">
                  {employees.filter((e) => e.is_active !== false).map((e) => (
                    <label key={e.id} className="flex items-center gap-2 text-xs py-0.5 cursor-pointer hover:bg-gray-50 rounded px-1">
                      <input type="checkbox" checked={(form.members || []).includes(e.id)}
                        onChange={(ev) => {
                          const ms = new Set(form.members || []);
                          if (ev.target.checked) ms.add(e.id); else ms.delete(e.id);
                          setForm({ ...form, members: Array.from(ms) });
                        }} />
                      {e.name} <span className="text-gray-400">({e.emp_id})</span>
                    </label>
                  ))}
                </div>
              </div>
              <div className="col-span-2 border-t pt-3 mt-1">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={!!form.is_recurring}
                    onChange={(e) => setForm({ ...form, is_recurring: e.target.checked })}
                    data-testid="project-is-recurring" />
                  <span className="font-semibold">🔁 Recurring project</span>
                  <span className="text-xs text-gray-500">(retainer / monthly engagement)</span>
                </label>
                {form.is_recurring && (
                  <div className="grid grid-cols-2 gap-3 mt-2 pl-6">
                    <div>
                      <Label className="text-xs">Cadence</Label>
                      <select className="w-full border rounded px-3 py-2 text-sm bg-white"
                        value={form.recurrence || 'monthly'}
                        onChange={(e) => setForm({ ...form, recurrence: e.target.value })}
                        data-testid="project-recurrence">
                        <option value="weekly">Weekly</option>
                        <option value="monthly">Monthly</option>
                        <option value="quarterly">Quarterly</option>
                        <option value="yearly">Yearly</option>
                      </select>
                    </div>
                    <div>
                      <Label className="text-xs">{form.recurrence === 'weekly' ? 'Day of week (1=Mon)' : 'Day of period (1–28)'}</Label>
                      <Input type="number" min="1" max={form.recurrence === 'weekly' ? 7 : 28}
                        value={form.recurrence_day || 1}
                        onChange={(e) => setForm({ ...form, recurrence_day: parseInt(e.target.value, 10) || 1 })} />
                    </div>
                  </div>
                )}
                {!form.is_recurring && (
                  <div className="grid grid-cols-3 gap-3 mt-3 pl-6" data-testid="contract-block">
                    <div className="col-span-2">
                      <Label className="text-xs">💰 Total Contract Value</Label>
                      <Input type="number" min="0" step="0.01"
                        value={form.contract_value || 0}
                        onChange={(e) => setForm({ ...form, contract_value: parseFloat(e.target.value) || 0 })}
                        placeholder="e.g. 500000"
                        data-testid="project-contract-value" />
                      <p className="text-[10px] text-gray-500 mt-0.5">Fixed-scope price. Invoices linked to this project are tracked against this.</p>
                    </div>
                    <div>
                      <Label className="text-xs">Currency</Label>
                      <select className="w-full border rounded px-3 py-2 text-sm bg-white"
                        value={form.contract_currency || 'INR'}
                        onChange={(e) => setForm({ ...form, contract_currency: e.target.value })}
                        data-testid="project-contract-currency">
                        {['INR', 'USD', 'SGD', 'MYR', 'AUD', 'EUR', 'GBP', 'AED'].map((c) => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>
                  </div>
                )}
              </div>
              <div className="col-span-2">
                <Label className="text-xs">Description</Label>
                <textarea className="w-full border rounded px-3 py-2 text-sm min-h-[60px]" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" onClick={() => setShow(false)}>Cancel</Button>
              <Button onClick={submit} data-testid="save-project-btn">{editing ? 'Update' : 'Create'} Project</Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
